/**
 * Authentication Middleware
 * Verifies Auth0 access tokens and attaches user info to request
 */

import type { FastifyRequest, FastifyReply } from 'fastify';
import { verifyAuth0Token } from './auth0.js';
import type { AuthUser } from './auth0.js';

// Extended user type re-exported for convenience
export { type AuthUser } from './auth0.js';

// Extend FastifyRequest to include user
declare module 'fastify' {
  interface FastifyRequest {
    user?: AuthUser;
  }
}

/**
 * Extract token from Authorization header
 * Format: "Bearer <token>"
 */
function extractToken(request: FastifyRequest): string | null {
  const authHeader = request.headers.authorization;
  if (!authHeader) {
    return null;
  }

  const parts = authHeader.split(' ');
  if (parts.length !== 2 || parts[0] !== 'Bearer') {
    return null;
  }

  return parts[1];
}

/**
 * Verify authentication - require valid Firebase ID token
 * Attaches decoded token to request.user
 */
export async function requireAuth(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  const token = extractToken(request);

  if (!token) {
    return reply.code(401).send({
      error: 'Unauthorized',
      message: 'Missing or invalid authorization header. Expected format: Bearer <token>',
    });
  }

  try {
    request.user = await verifyAuth0Token(token, request.log);
  } catch (error) {
    request.log.error({ err: error }, '[Auth] Token verification failed');
    return reply.code(401).send({
      error: 'Unauthorized',
      message: 'Invalid or expired token',
    });
  }
}

/**
 * Optional authentication - attach user if token provided
 * Does not fail if token missing
 */
export async function optionalAuth(
  request: FastifyRequest,
  _reply: FastifyReply
): Promise<void> {
  const token = extractToken(request);

  if (!token) {
    // No token provided, continue without user
    return;
  }

  try {
    request.user = await verifyAuth0Token(token, request.log);
  } catch (error) {
    // Invalid token, log but continue without user
    request.log.warn({ err: error }, '[Auth] Optional auth token verification failed');
  }
}

/**
 * Require admin role
 * Must be used after requireAuth
 */
export async function requireAdmin(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  if (!request.user) {
    return reply.code(401).send({
      error: 'Unauthorized',
      message: 'Authentication required',
    });
  }

  if (!request.user.admin) {
    return reply.code(403).send({
      error: 'Forbidden',
      message: 'Admin role required',
    });
  }
}

/**
 * Require premium subscription
 * Must be used after requireAuth
 */
export async function requirePremium(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  if (!request.user) {
    return reply.code(401).send({
      error: 'Unauthorized',
      message: 'Authentication required',
    });
  }

  const isPremium = request.user.premium === true;
  const hasActiveSubscription = request.user.subscriptionStatus === 'active';

  if (!isPremium && !hasActiveSubscription) {
    return reply.code(403).send({
      error: 'Forbidden',
      message: 'Premium subscription required',
    });
  }
}

/**
 * Verify user owns resource
 * Compares request.user.uid with provided userId
 */
export function requireOwnership(userId: string) {
  return async (request: FastifyRequest, reply: FastifyReply): Promise<void> => {
    if (!request.user) {
      return reply.code(401).send({
        error: 'Unauthorized',
        message: 'Authentication required',
      });
    }

    if (request.user.uid !== userId) {
      return reply.code(403).send({
        error: 'Forbidden',
        message: 'Access denied - resource owned by another user',
      });
    }
  };
}
